/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { BaseFilter } from '../BaseFilter';
import { Checkbox } from '../../inputs/Checkbox';
import { Popup } from '../../Popup';
import './MultiSelectBaseStyles.scss';
/**
 * Default option renderer. Returns a plain string when there is no icon or subtitle
 * (backwards compatible). Returns structured JSX when icon or subtitle is present.
 */
function renderDefaultOption(opt) {
    if (!opt.icon && !opt.subtitle)
        return opt.label;
    return (React.createElement("span", { className: "qmb-ui-multi-select-base__option-content" },
        opt.icon && (React.createElement("span", { className: "qmb-ui-multi-select-base__option-icon", "aria-hidden": "true" }, opt.icon)),
        React.createElement("span", { className: "qmb-ui-multi-select-base__option-text" },
            React.createElement("span", { className: "qmb-ui-multi-select-base__option-label" }, opt.label),
            opt.subtitle && React.createElement("span", { className: "qmb-ui-multi-select-base__option-subtitle" }, opt.subtitle))));
}
export function MultiSelectBase({ label, value, onChange, options, placeholder = 'Search', disabled = false, showReset = true, renderOption, onSearch, isLoading = false, onEndReached, includeInactive = false, onIncludeInactiveChange, includeInactiveLabel = 'Include inactive' }) {
    const [search, setSearch] = useState('');
    const listRef = useRef(null);
    const onEndReachedRef = useRef(onEndReached);
    const isLoadingRef = useRef(isLoading);
    const detachScrollRef = useRef(null);
    const normalizedSearch = search.toLowerCase();
    const filteredOptions = options.filter((option) => {
        const matchesLabel = option.label.toLowerCase().includes(normalizedSearch);
        if (!onSearch)
            return matchesLabel;
        if (!normalizedSearch)
            return true;
        if (option.isSentinel)
            return matchesLabel;
        return true;
    });
    const handleSearch = useCallback((q) => {
        setSearch(q);
        onSearch === null || onSearch === void 0 ? void 0 : onSearch(q);
    }, [onSearch]);
    const handleToggle = useCallback((optValue) => {
        const next = value.includes(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue];
        onChange(next);
    }, [value, onChange]);
    const handleReset = useCallback(() => {
        setSearch('');
        onSearch === null || onSearch === void 0 ? void 0 : onSearch('');
        onChange([]);
    }, [onChange, onSearch]);
    const handleOpenChange = useCallback((open) => {
        if (open)
            return;
        setSearch('');
        onSearch === null || onSearch === void 0 ? void 0 : onSearch('');
    }, [onSearch]);
    const selectedDisplay = useMemo(() => {
        if (value.length === 0)
            return '';
        const labelsByValue = new Map(options.map((option) => [option.value, option.label]));
        return value.map((selectedValue) => { var _a; return (_a = labelsByValue.get(selectedValue)) !== null && _a !== void 0 ? _a : String(selectedValue); }).join(', ');
    }, [options, value]);
    useEffect(() => {
        onEndReachedRef.current = onEndReached;
    }, [onEndReached]);
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);
    const bindListRef = useCallback((node) => {
        var _a;
        (_a = detachScrollRef.current) === null || _a === void 0 ? void 0 : _a.call(detachScrollRef);
        detachScrollRef.current = null;
        listRef.current = node;
        if (!node)
            return;
        const handleScroll = () => {
            var _a;
            if (isLoadingRef.current)
                return;
            if (node.scrollTop + node.clientHeight >= node.scrollHeight - 8) {
                (_a = onEndReachedRef.current) === null || _a === void 0 ? void 0 : _a.call(onEndReachedRef);
            }
        };
        node.addEventListener('scroll', handleScroll);
        detachScrollRef.current = () => node.removeEventListener('scroll', handleScroll);
    }, []);
    useEffect(() => () => {
        var _a;
        (_a = detachScrollRef.current) === null || _a === void 0 ? void 0 : _a.call(detachScrollRef);
        detachScrollRef.current = null;
    }, []);
    return (React.createElement(BaseFilter, { label: label, isActive: value.length > 0, selectedDisplay: selectedDisplay, count: value.length > 0 ? value.length : undefined, disabled: disabled, onReset: showReset && value.length > 0 ? handleReset : undefined, onOpenChange: handleOpenChange }, () => (React.createElement("div", { className: "qmb-ui-multi-select-base__popup" },
        React.createElement("div", { className: "qmb-ui-multi-select-base__search" },
            React.createElement(Popup.SearchInput, { value: search, placeholder: placeholder, onChange: handleSearch })),
        isLoading && React.createElement("div", { className: "qmb-ui-multi-select-base__loading" }, "Loading\u2026"),
        React.createElement("ul", { ref: bindListRef, className: "qmb-ui-multi-select-base__list", role: "listbox" },
            filteredOptions.map((opt) => {
                const optKey = `${typeof opt.value}:${String(opt.value)}`;
                return (React.createElement("li", { key: optKey, className: "qmb-ui-multi-select-base__item", role: "option", "aria-selected": value.includes(opt.value) },
                    React.createElement(Checkbox, { id: `qmb-ui-multi-select-base-opt-${optKey}`, checked: value.includes(opt.value), onChange: () => handleToggle(opt.value), label: renderOption ? renderOption(opt) : renderDefaultOption(opt) })));
            }),
            filteredOptions.length === 0 && !isLoading && (React.createElement("li", { className: "qmb-ui-multi-select-base__empty" }, "No options found"))),
        onIncludeInactiveChange && (React.createElement("div", { className: "qmb-ui-multi-select-base__include-inactive" },
            React.createElement(Checkbox, { id: "qmb-ui-multi-select-base-include-inactive", checked: includeInactive, onChange: onIncludeInactiveChange, label: includeInactiveLabel })))))));
}
